import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { validateQuoteLink } from "../lib/api";
import { getProductsBySubject, productGroups } from "../lib/products";
import { useCart } from "../lib/cartStore";
import { ProductCard } from "../components/ProductCard";
import { CartSidebar } from "../components/CartSidebar";
import { SubjectSection } from "../components/SubjectSection";
import type { QuoteValidationResult } from "../lib/types";

type LoadingState = "loading" | "valid" | "invalid" | "error";

export function QuotePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loadingState, setLoadingState] = useState<LoadingState>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [quoteData, setQuoteData] = useState<QuoteValidationResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { isEmpty, entries, getTotals, setQuoteContext, addToCart, clearCart } = useCart();
  const cartInitialized = useRef(false);

  const productsBySubject = getProductsBySubject();

  // Validate quote link on mount
  useEffect(() => {
    async function validateLink() {
      const data = searchParams.get("d");
      const sig = searchParams.get("s");

      if (!data || !sig) {
        setLoadingState("invalid");
        setErrorMessage("Missing required parameters. Please use a valid quote link.");
        return;
      }

      try {
        const result = await validateQuoteLink(data, sig);

        if (!result.valid) {
          setLoadingState("invalid");
          setErrorMessage(result.reason || "This link is no longer valid.");
          return;
        }

        setQuoteData(result);
        setLoadingState("valid");
      } catch (err) {
        setLoadingState("error");
        setErrorMessage(
          err instanceof Error ? err.message : "Failed to validate link. Please try again."
        );
      }
    }

    validateLink();
  }, [searchParams]);

  // Pre-populate cart and set quote context when quote data is available
  useEffect(() => {
    if (loadingState !== "valid" || !quoteData || cartInitialized.current) return;

    // Mark as initialized to prevent re-running
    cartInitialized.current = true;

    // Clear any existing cart items first
    clearCart();

    // Set quote context for checkout (school locked, contact pre-filled).
    // H6: carry the raw signed link params (d/s) so checkout can forward them
    // for server-side verification — previously they were dropped here and the
    // server had to trust client-supplied discounts.
    setQuoteContext({
      schoolId: quoteData.school_id || "",
      schoolName: quoteData.school_name || "",
      quoteData: searchParams.get("d") || undefined,
      quoteSig: searchParams.get("s") || undefined,
      contact: quoteData.contact
        ? {
            firstName: quoteData.contact.first_name,
            lastName: quoteData.contact.last_name,
            email: quoteData.contact.email,
            phone: quoteData.contact.phone,
          }
        : undefined,
      adminFirstName: quoteData.admin_first_name,
      adminLastName: quoteData.admin_last_name,
      adminEmail: quoteData.admin_email,
      paymentMethod: quoteData.payment_method,
      manualDiscounts: quoteData.discount_items?.map((d: { description: string; unitPriceExGstCents: number }) => ({
        description: d.description,
        amountCents: Math.abs(d.unitPriceExGstCents),
      })),
    });

    // Pre-populate cart with items from quote
    if (quoteData.items) {
      for (const item of quoteData.items) {
        const group = productGroups[item.product_id];
        if (group) {
          addToCart(group, item.quantity, item.variant_id);
        }
      }
    }
  }, [loadingState, quoteData, setQuoteContext, addToCart, clearCart, searchParams]);

  // Navigate to checkout page with cart data
  function handleCheckout() {
    if (isEmpty) return;

    setSubmitting(true);
    navigate("/checkout", {
      state: {
        entries,
        totals: getTotals(),
        quote: true,
        school: quoteData
          ? {
              acaraId: quoteData.school_id,
              name: quoteData.school_name,
              isManualEntry: false,
            }
          : undefined,
      },
    });
  }

  // Loading state
  if (loadingState === "loading") {
    return (
      <div className="order-page">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Validating your quote...</p>
        </div>
      </div>
    );
  }

  // Invalid or error state
  if (loadingState === "invalid" || loadingState === "error") {
    return (
      <div className="order-page">
        <header className="order-header">
          <h1>Quote Link Invalid</h1>
        </header>
        <div className="error-state">
          <div className="error-icon">!</div>
          <h2>{loadingState === "invalid" ? "Link Expired or Invalid" : "Something Went Wrong"}</h2>
          <p>{errorMessage}</p>
          <div className="error-actions">
            <Link to="/" className="btn btn-primary">
              Start a new order
            </Link>
            <a href="mailto:support@smartermaths.com.au" className="btn btn-secondary">
              Contact support
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Valid state - show order form with pre-populated cart
  return (
    <div className="order-page">
      <header className="order-header">
        <img src="https://smartered.wpengine.com/wp-content/uploads/2023/01/SmarterEd-logo.svg" alt="SmarterEd" className="order-header-logo" />
        <div className="order-header-text">
          <h1>Your Quote</h1>
          <p>Review your pre-configured order below</p>
        </div>
      </header>

      {/* School banner - read-only */}
      {quoteData?.school_name && (
        <div className="quote-school-banner">
          <span className="quote-school-label">School:</span>
          <span className="quote-school-name">{quoteData.school_name}</span>
        </div>
      )}

      <div className="order-layout">
        <main className="catalog-section">
          {/* Product catalog - pre-populated items shown in cart */}
          {Array.from(productsBySubject.entries()).map(([subject, products]) => (
            <SubjectSection key={subject.id} subject={subject} productIds={products.map((group) => group.id)}>
              <div className="product-grid">
                {products.map((group) => (
                  <ProductCard key={group.id} group={group} />
                ))}
              </div>
            </SubjectSection>
          ))}
        </main>

        <aside className="cart-section">
          <CartSidebar
            onSubmit={handleCheckout}
            onCancel={() => {}}
            submitting={submitting}
            isPunchOut={false}
          />
        </aside>
      </div>
    </div>
  );
}
